'use strict';

export default angular
  .module('maestro.services.data.auth', [
    'maestro.services.webcore'
  ])
  .service('AuthService', AuthService)
  .name;

function AuthService(
  webcore,
  config,
  $q,
  $state,
  $rootScope,
  $filter
) {
  const $t = $filter('translate');

  const APP_NAME = config.getConfigByKey('environment.appName');
  const DEFAULT_PWD_REGEX = '^.{8,}$';
  const DEFAULT_PASSCODE_RESEND_TIMEOUT = 120;

  // webcore.services.auth.onLogout.on(() => {
  //   if ($state.current.name !== 'signin') {
  //     $state.go('signin');
  //   }
  // });

  webcore.services.auth.onNeedRelogin.on(() => {
    if ($state.current.name !== 'signin') {
      $rootScope.errorMsg = $t('common.api-key-expired-logout-message');
      $state.go('signin');
    }
  });

  /**
   * Logs out any currently logged in user in the current browser
   * @returns {*}
   */
  this.logoutFromThisBrowser = () => {
    return $q.when(webcore.services.auth.logoutFromThisBrowser());
  };

  /**
   * Logs out any currently logged in user
   * @returns {*}
   */
  this.logoutFromAllBrowsers = () => {
    return $q.when(webcore.services.auth.logoutFromAllBrowsers());
  };

  /**
   * Get current API_KEY.
   * @returns {string}
   */
  this.getApiKey = () => {
    return webcore.services.auth.apiKey();
  };

  /**
   * Logs user into Maestro.
   * @param {string} username username
   * @param {string} password user password
   * @param {string} passcode code from sms
   * @param {boolean} [admin] key type
   * @param {number} [passcodeType] passcode type. 2 - email, 3 - sms, 4 - totp
   * @returns {Promise}
   */
  this.login = (username, password, passcode, admin, passcodeType) => {
    const brand = $rootScope.currentBrand?.brand || void 0;
    if (passcode) {
      if (passcodeType === 4) { // Login by TOTP
        return $q.when(webcore.services.auth.loginByTotp(username, password, passcode, APP_NAME, !!admin, brand));
      } else { // Login by Email/SMS passcode
        return $q.when(webcore.services.auth.loginByPasscode(username, passcode, APP_NAME, !!admin, brand));
      }
    } else { // Login by Username/Password
      return $q.when(webcore.services.auth.login(username, password, APP_NAME, !!admin, passcodeType, brand));
    }
  };

  /**
   * Login by User API_KEY.
   * @param {string} key Existing user/admin API_KEY
   * @return {Promise}
   */
  this.loginByApiKey = (key) => {
    const brand = $rootScope.currentBrand?.brand || void 0;
    return $q.when(webcore.services.auth.loginByKey(key, true, brand));
  };

  /**
   * Send SMS code to user.
   * @param {string} username
   * @param {boolean} [admin] Admin or User API_KEY.
   * @returns {Promise}
   */
  this.sendPasscode = (username, admin = true) => {
    const brand = $rootScope.currentBrand?.brand || void 0;
    return $q.when(webcore.services.auth.sendPasscode(username, admin, brand));
  };

  /**
   * Set new password.
   * If 2-factor auth is used - this API will send passcode, so you'll need to call this method again
   * @param {string} newPassword New password
   * @param {string} oldPassword Old password
   * @param {string} [passcode] 2-factor auth passcode
   * @param [params] Request parameters
   * @param {boolean} [params.strongPassword] Check if the password is strong
   * @param {boolean} [params.keepKeyVersion] Keep the current user API key
   * @returns {Promise}
   */
  this.setNewPassword = (newPassword, oldPassword, passcode, params) => {
    params = params || {};
    params.brand = $rootScope.currentBrand?.brand || void 0;
    return $q.when(webcore.services.auth.setNewPassword(newPassword, oldPassword, passcode, params));
  };

  /**
   * Get password strength regexp
   * @returns {Promise<string>}
   */
  this.getPasswordRegex = () => {
    return $q.when(webcore.services.systemProperties.getPasswordValidationRegex())
      .then(result => {
        return result || DEFAULT_PWD_REGEX;
      }, () => {
        return DEFAULT_PWD_REGEX;
      });
  };

  /**
   * Get password strength regexp
   * @returns {Promise<number>}
   */
  this.getPasscodeResendTimeout = () => {
    return $q.when(webcore.services.systemProperties.getPasscodeResendTimeout())
      .then(result => {
        return (!!result && result > 0) ? result : DEFAULT_PASSCODE_RESEND_TIMEOUT;
      }, () => {
        return DEFAULT_PASSCODE_RESEND_TIMEOUT;
      });
  };

  /**
   * New operation token provider
   * @returns {Class}
   */
  this.operationToken = () => {
    const USER_REGISTRATION_TOKEN = 1;
    return webcore.services.operationToken.prepareToken(USER_REGISTRATION_TOKEN);
  };

  /**
   * Check authentication status
   * @returns {boolean}
   */
  this.isAuthenticated = () => {
    return webcore.services.auth.isAuthenticated();
  };

  // #region -------------------- Time-based one-time password (TOTP) --------------------

  /**
   * Generate Time-based One-Time Password (TOTP) Secret and QR code URL.
   * The client has to confirm that the secret has been set in the Authenticator app by submitting codes multiple times (usually two).
   *
   * All TOTP factor management APIs require 2-step authentication (administrator API key).
   * Google Authenticator and Microsoft Authenticator apps are supported.
   *
   * See {@link https://iotapps.docs.apiary.io/#reference/authentication/totp/create-totp-secret}
   *
   * @param params Request parameters.
   * @param {string} params.name Device name used to store the authentication factor
   * @param {string} [params.issuer] Optional issuer returned in the QR code URL and be displayed in the app
   *
   * returns {Promise<CreateTotpSecretApiResponse>}
   */
  this.createTotpSecret = (params) => {
    return $q.when(webcore.services.auth.createTotpSecret(params));
  };

  /**
   * The client has to confirm that the secret has been set in the Authenticator app
   * by submitting code.
   *
   * The API returns the authentication factor's status.
   * The value 1 means that the factor is active and can be used.
   * Zero or negative value means that additional confirmations are required.
   * On each successful confirmation the status value is decremented starting from zero.
   *
   * See {@link https://iotapps.docs.apiary.io/#reference/authentication/totp/confirm-totp-secret}
   *
   * @param {string} name Device name used to store the authentication factor
   * @param {string} code Code generated in the Authenticator app
   *
   * returns {Promise<ConfirmTotpSecretApiResponse>}
   */
  this.confirmTotpSecret = (name, code) => {
    return $q.when(webcore.services.auth.confirmTotpSecret(name, code));
  };

  /**
   * Return active and not expired user's TOTP factors.
   *
   * See {@link https://iotapps.docs.apiary.io/#reference/authentication/totp/get-totp-factors}
   *
   * returns {Promise<GetTotpFactorsApiResponse>}
   */
  this.getTotpFactors = () => {
    return $q.when(webcore.services.auth.getTotpFactors());
  };

  /**
   * Delete TOTP factor
   *
   * See {@link https://iotapps.docs.apiary.io/#reference/authentication/totp/delete-totp-factor}
   *
   * @param {string} name Authentication factor name to delete
   *
   * returns {Promise<DeleteTotpFactorApiResponse>}
   */
  this.deleteTotpFactor = (name) => {
    return $q.when(webcore.services.auth.deleteTotpFactor(name));
  };

  // #endregion

}
